import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Mail, Phone, MapPin, Clock, Send, CheckCircle2,
  Building2, Truck, Landmark
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { SectionHeader } from '../components/shared/SectionHeader';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const industries = [
  { value: 'fleet', label: 'Fleet & Logistics', icon: Truck },
  { value: 'commercial', label: 'Commercial Real Estate', icon: Building2 },
  { value: 'municipal', label: 'Municipality / Government', icon: Landmark },
  { value: 'other', label: 'Other' }
];

const projectTypes = [
  { value: 'new', label: 'New Installation' },
  { value: 'expansion', label: 'Expand Existing' },
  { value: 'caas', label: 'Charging-as-a-Service' },
  { value: 'consultation', label: 'General Consultation' }
];

const timelines = [
  { value: 'immediate', label: 'Immediate (< 3 months)' },
  { value: 'near', label: 'Near-term (3-6 months)' },
  { value: 'planning', label: 'Planning Phase (6-12 months)' },
  { value: 'exploring', label: 'Just Exploring' }
];

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    industry: '',
    project_type: '',
    timeline: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/leads`, {
        ...formData,
        submission_type: 'proposal'
      });
      setIsSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again or contact us directly.');
      console.error('Form submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-slate-50 circuit-pattern" data-testid="contact-hero">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#00A2B5] mb-4 block">
              Contact Us
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 mb-6">
              Let's{' '}
              <span className="gradient-text">Lead the Charge</span>{' '}
              Together
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Whether you're electrifying a fleet, upgrading your property with EV charging, or deploying large-scale infrastructure, our team is ready to help.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="contact-form-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-5 gap-16">
            {/* Contact Info */}
            <div className="lg:col-span-2">
              <SectionHeader
                overline="Get in Touch"
                title="Contact Information"
              />

              <div className="space-y-6 mb-10">
                <a 
                  href="mailto:info@SkytecEV.com"
                  className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-[#00A2B5] transition-colors group"
                  data-testid="contact-email"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00A2B5] to-[#36D890] flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 group-hover:text-[#00A2B5] transition-colors">Email Us</p>
                    <p className="text-slate-600">info@SkytecEV.com</p>
                  </div>
                </a>

                <a 
                  href="tel:+15619471590"
                  className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-[#00A2B5] transition-colors group"
                  data-testid="contact-phone"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00A2B5] to-[#36D890] flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 group-hover:text-[#00A2B5] transition-colors">Call Us</p>
                    <p className="text-slate-600">(561) 947-1590</p>
                  </div>
                </a>

                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00A2B5] to-[#36D890] flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Business Hours</p>
                    <p className="text-slate-600">Mon–Fri, 8 AM–6 PM EST</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00A2B5] to-[#36D890] flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Headquarters</p>
                    <p className="text-slate-600">5092 SW 73rd Ave, Davie, FL 33314</p>
                    <p className="text-sm text-slate-500 mt-1">Nationwide Coverage</p>
                  </div>
                </div>
              </div>

              {/* Trust indicators */}
              <div className="p-6 bg-gradient-to-br from-[#00A2B5]/5 to-[#36D890]/5 rounded-xl border border-[#00A2B5]/20">
                <p className="text-sm font-semibold text-slate-900 mb-4">Why Work With Skytec EV?</p>
                <ul className="space-y-3">
                  {[
                    '500+ successful deployments',
                    'Nationwide coverage (50 states)',
                    '99.5% average uptime',
                    'Dedicated project management'
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle2 className="w-4 h-4 text-[#00C5A5] flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl p-8 md:p-10 border border-slate-200 shadow-sm">
                {isSubmitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                    data-testid="form-success"
                  >
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#00A2B5] to-[#36D890] flex items-center justify-center mx-auto mb-6">
                      <CheckCircle2 className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-black text-slate-900 mb-3">Thank You!</h3>
                    <p className="text-slate-600 mb-6 max-w-md mx-auto">
                      We've received your request and will get back to you within 1-2 business days with a tailored plan.
                    </p>
                    <button
                      onClick={() => {
                        setIsSubmitted(false);
                        setFormData({
                          name: '', email: '', company: '', phone: '',
                          industry: '', project_type: '', timeline: '', message: ''
                        });
                      }}
                      className="text-[#00A2B5] font-semibold hover:underline"
                    >
                      Submit Another Request
                    </button>
                  </motion.div>
                ) : (
                  <>
                    <div className="mb-8">
                      <h3 className="text-2xl font-black text-slate-900 mb-2">Request a Proposal</h3>
                      <p className="text-slate-600">
                        Tell us about your sites, timelines, and constraints—we'll respond with a clear, actionable plan.
                      </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                      {/* Name & Email */}
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="name" className="text-slate-700 font-medium mb-2 block">
                            Full Name *
                          </Label>
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            placeholder="John Smith"
                            className="h-12"
                            data-testid="input-name"
                          />
                        </div>
                        <div>
                          <Label htmlFor="email" className="text-slate-700 font-medium mb-2 block">
                            Work Email *
                          </Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            placeholder="john@company.com"
                            className="h-12"
                            data-testid="input-email"
                          />
                        </div>
                      </div>

                      {/* Company & Phone */}
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="company" className="text-slate-700 font-medium mb-2 block">
                            Company
                          </Label>
                          <Input
                            id="company"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            placeholder="Company Name"
                            className="h-12"
                            data-testid="input-company"
                          />
                        </div>
                        <div>
                          <Label htmlFor="phone" className="text-slate-700 font-medium mb-2 block">
                            Phone
                          </Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="(555) 123-4567"
                            className="h-12"
                            data-testid="input-phone"
                          />
                        </div>
                      </div>

                      {/* Industry */}
                      <div>
                        <Label className="text-slate-700 font-medium mb-2 block">
                          Industry *
                        </Label>
                        <Select 
                          value={formData.industry} 
                          onValueChange={(value) => handleSelectChange('industry', value)}
                          required
                        >
                          <SelectTrigger className="h-12" data-testid="select-industry">
                            <SelectValue placeholder="Select your industry" />
                          </SelectTrigger>
                          <SelectContent>
                            {industries.map((ind) => (
                              <SelectItem key={ind.value} value={ind.value}>
                                {ind.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Project Type & Timeline */}
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div>
                          <Label className="text-slate-700 font-medium mb-2 block">
                            Project Type
                          </Label>
                          <Select 
                            value={formData.project_type} 
                            onValueChange={(value) => handleSelectChange('project_type', value)}
                          >
                            <SelectTrigger className="h-12" data-testid="select-project-type">
                              <SelectValue placeholder="Select project type" />
                            </SelectTrigger>
                            <SelectContent>
                              {projectTypes.map((type) => (
                                <SelectItem key={type.value} value={type.value}>
                                  {type.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-slate-700 font-medium mb-2 block">
                            Timeline
                          </Label>
                          <Select 
                            value={formData.timeline} 
                            onValueChange={(value) => handleSelectChange('timeline', value)}
                          >
                            <SelectTrigger className="h-12" data-testid="select-timeline">
                              <SelectValue placeholder="Select timeline" />
                            </SelectTrigger>
                            <SelectContent>
                              {timelines.map((tl) => (
                                <SelectItem key={tl.value} value={tl.value}>
                                  {tl.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Message */}
                      <div>
                        <Label htmlFor="message" className="text-slate-700 font-medium mb-2 block">
                          Project Details *
                        </Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          placeholder="Tell us about your sites, number of chargers needed, current electrical capacity, and any specific requirements..."
                          rows={5}
                          className="resize-none"
                          data-testid="textarea-message"
                        />
                      </div>

                      {error && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                          {error}
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="btn-gradient w-full h-14 rounded-full font-semibold text-lg inline-flex items-center justify-center gap-2 disabled:opacity-70"
                        data-testid="submit-button"
                      >
                        {isSubmitting ? (
                          <>
                            <span className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            <Send className="w-5 h-5" />
                            Submit Request
                          </>
                        )}
                      </button>

                      <p className="text-xs text-slate-500 text-center">
                        By submitting this form, you agree to our privacy policy and consent to be contacted about Skytec EV services.
                      </p>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
